import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const MODEL_OPTIONS = [
  { provider: "anthropic", model: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 — recommended" },
  { provider: "anthropic", model: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 — fast" },
  { provider: "openai", model: "gpt-5.2", label: "GPT-5.2" },
  { provider: "openai", model: "gpt-5.1", label: "GPT-5.1" },
  { provider: "gemini", model: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { provider: "gemini", model: "gemini-3-flash-preview", label: "Gemini 3 Flash (preview)" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inboxTesting, setInboxTesting] = useState(false);
  const [checkingInbox, setCheckingInbox] = useState(false);
  const [apolloStatus, setApolloStatus] = useState({ connected: false });
  const [lushaStatus, setLushaStatus] = useState({ connected: false });
  const [lushaTesting, setLushaTesting] = useState(false);
  const [lushaInfo, setLushaInfo] = useState(null);
  const [instantlyStatus, setInstantlyStatus] = useState({ connected: false });
  const [apolloTesting, setApolloTesting] = useState(false);
  const [instantlyTesting, setInstantlyTesting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const [voiceList, setVoiceList] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);

  const [aiCallStatus, setAiCallStatus] = useState({ ai_call_provider: "vapi", vapi_connected: false, bland_connected: false });

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/apollo/status").then((r) => setApolloStatus(r.data)).catch(() => {});
    api.get("/lusha/status").then((r) => setLushaStatus(r.data)).catch(() => {});
    api.get("/instantly/status").then((r) => setInstantlyStatus(r.data)).catch(() => {});
    api.get("/ai-calls/status").then((r) => setAiCallStatus(r.data)).catch(() => {});
  }, []);

  if (!s) return <Layout title="Settings"><div className="text-sm text-muted-foreground">Loading…</div></Layout>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        ai_provider: s.ai_provider,
        ai_model: s.ai_model,
        sender_email: s.sender_email || null,
        newsletter_from_emails: s.newsletter_from_emails || [],
        instantly_newsletter_campaign_id: s.instantly_newsletter_campaign_id || null,
        linkedin_account_id: s.linkedin_account_id || null,
        linkedin_inmails_disabled: s.linkedin_inmails_disabled === true ? true : (s.linkedin_inmails_disabled === false ? false : null),
        sender_name: s.sender_name || null,
        signature: s.signature || null,
        booking_link: s.booking_link || null,
        company_name: s.company_name,
        value_prop: s.value_prop,
        auto_send: !!s.auto_send,
        daily_send_cap: Number(s.daily_send_cap) || 50,
        imap_enabled: !!s.imap_enabled,
        imap_host: s.imap_host || null,
        imap_port: Number(s.imap_port) || 993,
        imap_username: s.imap_username || null,
        imap_password: s.imap_password || null,
        imap_folder: s.imap_folder || "INBOX",
        apollo_api_key: s.apollo_api_key ?? null,
        lusha_api_key: s.lusha_api_key ?? null,
        instantly_api_key: s.instantly_api_key ?? null,
        instantly_default_campaign_id: s.instantly_default_campaign_id ?? null,
        instantly_webhook_secret: s.instantly_webhook_secret ?? null,
        sender_provider: s.sender_provider || "resend",
        perplexity_api_key: s.perplexity_api_key ?? null,
        tavily_api_key: s.tavily_api_key ?? null,
        triggers_enabled: s.triggers_enabled !== false,
        daily_action_target: Number(s.daily_action_target) || 8,
        twilio_account_sid: s.twilio_account_sid ?? null,
        twilio_auth_token: s.twilio_auth_token ?? null,
        twilio_phone_number: s.twilio_phone_number ?? null,
        twilio_user_phone: s.twilio_user_phone ?? null,
        elevenlabs_api_key: s.elevenlabs_api_key ?? null,
        elevenlabs_voice_id: s.elevenlabs_voice_id ?? null,
        ai_call_provider: s.ai_call_provider || "vapi",
        vapi_api_key: s.vapi_api_key ?? null,
        vapi_phone_number_id: s.vapi_phone_number_id ?? null,
        vapi_webhook_secret: s.vapi_webhook_secret ?? null,
        bland_api_key: s.bland_api_key ?? null,
        bland_voice_id: s.bland_voice_id ?? null,
        ai_call_proactive_enabled: !!s.ai_call_proactive_enabled,
        ai_call_proactive_daily_cap: Number(s.ai_call_proactive_daily_cap) || 5,
        ai_call_proactive_min_touches: Number(s.ai_call_proactive_min_touches) || 2,
        auto_runner_enabled: !!s.auto_runner_enabled,
        auto_runner_mode: s.auto_runner_mode || "draft_only",
        auto_runner_cap_mode: s.auto_runner_cap_mode || "soft_ceiling",
        auto_runner_skip_weekend_email: s.auto_runner_skip_weekend_email !== false,
        auto_runner_timezone: s.auto_runner_timezone || "Australia/Sydney",
        auto_runner_schedule: Array.isArray(s.auto_runner_schedule) ? s.auto_runner_schedule : [],
        morning_briefing_enabled: !!s.morning_briefing_enabled,
        morning_briefing_time: s.morning_briefing_time || "08:00",
        morning_briefing_recipient: s.morning_briefing_recipient ?? null,
        action_list_enabled: !!s.action_list_enabled,
        action_list_time: s.action_list_time || "08:30",
        action_list_recipient: s.action_list_recipient ?? null,
        reactivation_enabled: s.reactivation_enabled !== false,
        apify_api_token: s.apify_api_token ?? null,
        apify_actor_id: s.apify_actor_id ?? null,
        auto_promote_linkedin_only: s.auto_promote_linkedin_only === true,
      });
      setS(data);
      toast.success("Settings saved");
      // refresh integration status pills
      api.get("/apollo/status").then((r) => setApolloStatus(r.data)).catch(() => {});
    api.get("/lusha/status").then((r) => setLushaStatus(r.data)).catch(() => {});
      api.get("/instantly/status").then((r) => setInstantlyStatus(r.data)).catch(() => {});
      api.get("/ai-calls/status").then((r) => setAiCallStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testInbox = async () => {
    setInboxTesting(true);
    try {
      const { data } = await api.post("/inbox/test", {
        imap_host: s.imap_host,
        imap_port: Number(s.imap_port) || 993,
        imap_username: s.imap_username,
        imap_password: s.imap_password,
        imap_folder: s.imap_folder || "INBOX",
      });
      if (data.ok) toast.success("Connected — IMAP credentials work");
      else toast.error(`Connection failed: ${data.error}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setInboxTesting(false);
    }
  };

  const checkInboxNow = async () => {
    setCheckingInbox(true);
    try {
      const { data } = await api.post("/inbox/check-now");
      if (!data.checked) toast.error(`Inbox not ready: ${data.reason}`);
      else if (data.ok) toast.success(`Checked — ${data.fetched} fetched, ${data.matched} matched, ${data.classified} classified`);
      else toast.error(`Check failed: ${data.error}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check failed");
    } finally {
      setCheckingInbox(false);
    }
  };

  const onModelChange = (combined) => {
    const opt = MODEL_OPTIONS.find((o) => `${o.provider}::${o.model}` === combined);
    if (opt) setS({ ...s, ai_provider: opt.provider, ai_model: opt.model });
  };

  const testApollo = async () => {
    setApolloTesting(true);
    try {
      const { data } = await api.post("/apollo/test");
      if (data.ok) toast.success("Apollo: key works");
      else toast.error(`Apollo: ${data.error}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apollo test failed");
    } finally {
      setApolloTesting(false);
    }
  };

  const testLusha = async () => {
    setLushaTesting(true);
    try {
      const { data } = await api.post("/lusha/test");
      if (data.ok) {
        setLushaInfo({ remaining: data.remaining_credits, plan: data.plan });
        toast.success(`Lusha: key works · ${data.remaining_credits ?? "?"} credits left · plan: ${data.plan ?? "—"}`);
      } else {
        toast.error(`Lusha: ${data.error}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lusha test failed");
    } finally {
      setLushaTesting(false);
    }
  };

  const testInstantly = async () => {
    setInstantlyTesting(true);
    try {
      const { data } = await api.post("/instantly/test");
      if (data.ok) toast.success(`Instantly: key works (${data.campaign_count_sample ?? 0} sample campaign)`);
      else toast.error(`Instantly: ${data.error}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Instantly test failed");
    } finally {
      setInstantlyTesting(false);
    }
  };

  const loadInstantlyCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await api.get("/instantly/campaigns");
      setCampaigns(data.campaigns || []);
      if (!data.campaigns?.length) toast.error("No campaigns found in workspace");
      else toast.success(`Loaded ${data.campaigns.length} campaigns`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const webhookUrl = (process.env.REACT_APP_BACKEND_URL || "") + "/api/webhooks/instantly";
  const copyWebhook = async () => {
    try { await navigator.clipboard.writeText(webhookUrl); toast.success("Webhook URL copied"); } catch { toast.error("Copy failed"); }
  };

  const loadVoices = async () => {
    try {
      const { data } = await api.get("/voicemails/voices");
      setVoiceList(data || []);
      toast.success(`Loaded ${data?.length || 0} voices`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load voices");
    }
  };

  const previewVoicemail = async () => {
    setPreviewLoading(true);
    setPreviewAudio(null);
    try {
      // Use first lead with a real company for the preview
      const { data: leads } = await api.get("/leads", { params: { limit: 1 } });
      const lead = leads?.[0];
      if (!lead) { toast.error("Add at least one lead first"); return; }
      const { data } = await api.post("/voicemails/preview", { lead_id: lead.lead_id, voice_id: s.elevenlabs_voice_id });
      setPreviewAudio({ url: data.audio_url, script: data.script, lead });
      toast.success("Preview ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Layout title="Settings" subtitle="Configuration" actions={
      <Button onClick={save} disabled={saving} data-testid="settings-save" style={{ borderRadius: 2 }}>{saving ? "Saving…" : "Save"}</Button>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* AI */}
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">AI Engine</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Model</div>
          <p className="text-sm text-muted-foreground mb-4">Switch the AI model behind research, drafts, and briefs. Powered by your Emergent Universal Key.</p>
          <Select value={`${s.ai_provider}::${s.ai_model}`} onValueChange={onModelChange}>
            <SelectTrigger className="input-crisp" data-testid="settings-model"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((o) => <SelectItem key={`${o.provider}::${o.model}`} value={`${o.provider}::${o.model}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Booking */}
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Calendar</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Booking link</div>
          <p className="text-sm text-muted-foreground mb-4">Paste your Outlook (Microsoft Bookings) or Calendly link. AI emails referencing a booking link will use this.</p>
          <Input value={s.booking_link || ""} onChange={(e) => setS({ ...s, booking_link: e.target.value })} placeholder="https://outlook.office.com/bookwithme/…" className="input-crisp" data-testid="settings-booking-link" />
        </div>

        {/* Automation */}
        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Automation</div>
          <div className="font-serif text-xl tracking-tight mb-3" style={{ fontWeight: 450 }}>Sequence auto-advance</div>
          <p className="text-sm text-muted-foreground mb-4">
            The scheduler ticks every 60 seconds. It auto-drafts the next step's email for every active enrollment whose next-send time has passed, and auto-pauses the moment a lead replies, books a meeting, or moves to nurture.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-border p-4 flex items-start gap-4" style={{ borderRadius: 2 }}>
              <div className="flex-1">
                <div className="font-medium mb-1">Auto-send</div>
                <div className="text-xs text-muted-foreground">When ON, the scheduler will SEND each step's email automatically (respecting your daily cap). When OFF, drafts are queued for your review in the lead drawer.</div>
              </div>
              <Switch checked={!!s.auto_send} onCheckedChange={(v) => setS({ ...s, auto_send: v })} data-testid="settings-auto-send" />
            </div>
            <div className="border border-border p-4" style={{ borderRadius: 2 }}>
              <div className="font-medium mb-1">Daily send cap</div>
              <div className="text-xs text-muted-foreground mb-3">Max emails the scheduler will send in a single 24h window.</div>
              <Input
                type="number"
                value={s.daily_send_cap || 50}
                onChange={(e) => setS({ ...s, daily_send_cap: Number(e.target.value) })}
                className="input-crisp w-32"
                data-testid="settings-daily-cap"
              />
            </div>
          </div>
        </div>

        {/* Sender */}
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Email identity</div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Sender</div>
          <div className="space-y-3">
            <div>
              <Label className="overline">Sender name</Label>
              <Input value={s.sender_name || ""} onChange={(e) => setS({ ...s, sender_name: e.target.value })} className="mt-1 input-crisp" data-testid="settings-sender-name" />
            </div>
            <div>
              <Label className="overline">Sender email (must be verified in Resend)</Label>
              <Input value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })} placeholder="alex@yourdomain.com.au" className="mt-1 input-crisp" data-testid="settings-sender-email" />
            </div>
            <div>
              <Label className="overline">Newsletter rotation pool (random pick per send)</Label>
              <Textarea
                value={(s.newsletter_from_emails || []).join("\n")}
                onChange={(e) => setS({
                  ...s,
                  newsletter_from_emails: e.target.value
                    .split(/[\n,]/)
                    .map((x) => x.trim())
                    .filter(Boolean),
                })}
                rows={3}
                placeholder={"news@domain1.com.au\nupdates@domain2.com.au\nbrief@domain3.com.au"}
                className="mt-1 font-mono text-sm input-crisp"
                data-testid="settings-newsletter-from-emails"
              />
              <p className="text-xs text-muted-foreground mt-1">
                One per line. Each must be on a domain verified at resend.com/domains. The system picks one at random per newsletter send — spreads volume across your domains for better deliverability. Leave empty to use the single Sender email above.
              </p>
            </div>
            <div>
              <Label className="overline">LinkedIn account ID (Unipile)</Label>
              <Input
                value={s.linkedin_account_id || ""}
                onChange={(e) => setS({ ...s, linkedin_account_id: e.target.value.trim() })}
                placeholder="e.g. KsTUQoOVSAOI13DwzL2Qcg"
                className="mt-1 font-mono text-sm input-crisp"
                data-testid="settings-linkedin-account-id"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste here if you reconnected your LinkedIn in Unipile (e.g. to add the Sales Navigator source).
                Find it in your Unipile dashboard → Accounts → click your LinkedIn account → copy the ID from the URL or header.
                Updating this routes ALL future invites/InMails/DMs/voice-notes through the new account.
              </p>
            </div>
            <ResetLinkedInCard />
            <LandingPageCard />
            <div>
              <Label className="overline">Instantly newsletter campaign ID (preferred — skip Resend)</Label>
              <Input
                value={s.instantly_newsletter_campaign_id || ""}
                onChange={(e) => setS({ ...s, instantly_newsletter_campaign_id: e.target.value })}
                placeholder="e.g. 4f3c8a9b-1234-5678-90ab-cdef01234567"
                className="mt-1 font-mono text-sm input-crisp"
                data-testid="settings-instantly-newsletter-campaign-id"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Create a 1-step Instantly campaign with subject{" "}
                <code className="bg-muted px-1">{"{{step_1_subject}}"}</code> and body{" "}
                <code className="bg-muted px-1">{"{{step_1_body}}"}</code>, paste the campaign ID
                here, and newsletter sends will route through Instantly using your verified mailboxes
                (no Resend domain verification needed). Leave empty to use Resend.
              </p>
            </div>
            {s.linkedin_inmails_disabled && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="settings-inmails-disabled-banner">
                <p className="text-sm font-medium text-amber-900">
                  LinkedIn InMails are currently disabled
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Unipile returned 403 <code className="bg-amber-100 px-1">resource_access_restricted</code> on InMail send. Most likely one of:
                </p>
                <ol className="list-decimal list-inside text-xs text-amber-800 mt-1 space-y-0.5">
                  <li><strong>InMail credits exhausted</strong> — Sales Nav gives ~50/month. Check{" "}
                    <a href="https://www.linkedin.com/sales/inbox" target="_blank" rel="noreferrer" className="underline">linkedin.com/sales/inbox</a> → top right.</li>
                  <li><strong>Unipile auth missing InMail scope</strong> — reconnect your LinkedIn account in Unipile's hosted auth window.</li>
                  <li><strong>Sales Nav seat lacks InMail-send rights</strong> — some seat types (Team viewer-only) have search but no send.</li>
                </ol>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setS({ ...s, linkedin_inmails_disabled: false })}
                  data-testid="settings-reenable-inmails-btn"
                >
                  Re-enable InMails
                </Button>
              </div>
            )}
            <div>
              <Label className="overline">Signature</Label>
              <Textarea value={s.signature || ""} onChange={(e) => setS({ ...s, signature: e.target.value })} rows={4} className="mt-1 font-mono text-sm input-crisp" data-testid="settings-signature" />
            </div>
          </div>
        </div>

        {/* Account password */}
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Account</div>
          <div className="font-serif text-xl tracking-tight mb-2" style={{ fontWeight: 450 }}>Set / change password</div>
          <p className="text-sm text-muted-foreground mb-4">
            Adds a password to this account so you can sign in with email instead of Google. Your existing data (leads, sequences, integrations) stays on the same account — both sign-in methods work afterwards.
          </p>
          <SetPasswordCard />
        </div>

        {/* Company */}
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Positioning</div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Company &amp; value prop</div>
          <div className="space-y-3">
            <div>
              <Label className="overline">Company name</Label>
              <Input value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })} className="mt-1 input-crisp" data-testid="settings-company" />
            </div>
            <div>
              <Label className="overline">Value prop</Label>
              <Textarea value={s.value_prop} onChange={(e) => setS({ ...s, value_prop: e.target.value })} rows={3} className="mt-1 input-crisp" data-testid="settings-valueprop" />
            </div>
            <div>
              <Label className="overline">Target industries</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {s.target_industries.map((i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-secondary border border-border" style={{ borderRadius: 2 }}>{i}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Inbox Watcher</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>AI Reply Inbox</div>
          <p className="text-sm text-muted-foreground mb-4">
            Connect your sending mailbox via IMAP. Every 5 minutes the watcher polls for unread replies, matches each to a lead, runs the AI classifier, drops the result into the Qualification Queue, auto-pauses the active sequence, and bumps the lead to <strong className="text-foreground">engaged</strong>. Use an app-specific password (Gmail: aka App Password; Outlook: aka App Password under "Advanced security").
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">IMAP host</Label>
              <Input value={s.imap_host || ""} onChange={(e) => setS({ ...s, imap_host: e.target.value })} placeholder="imap.gmail.com or outlook.office365.com" className="mt-1 input-crisp" data-testid="imap-host" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Port</Label>
                <Input type="number" value={s.imap_port || 993} onChange={(e) => setS({ ...s, imap_port: Number(e.target.value) })} className="mt-1 input-crisp" data-testid="imap-port" />
              </div>
              <div>
                <Label className="overline">Folder</Label>
                <Input value={s.imap_folder || "INBOX"} onChange={(e) => setS({ ...s, imap_folder: e.target.value })} className="mt-1 input-crisp" data-testid="imap-folder" />
              </div>
            </div>
            <div>
              <Label className="overline">Username (email)</Label>
              <Input value={s.imap_username || ""} onChange={(e) => setS({ ...s, imap_username: e.target.value })} placeholder="alex@yourdomain.com.au" className="mt-1 input-crisp" data-testid="imap-username" />
            </div>
            <div>
              <Label className="overline">App password</Label>
              <Input type="password" value={s.imap_password || ""} onChange={(e) => setS({ ...s, imap_password: e.target.value })} placeholder="••••••••••••" className="mt-1 input-crisp" data-testid="imap-password" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <Switch checked={!!s.imap_enabled} onCheckedChange={(v) => setS({ ...s, imap_enabled: v })} data-testid="imap-enabled" />
              <span className="text-sm">Enable watcher</span>
            </div>
            <div className="flex-1" />
            <Button variant="outline" onClick={testInbox} disabled={inboxTesting || !s.imap_host || !s.imap_username || !s.imap_password} data-testid="imap-test" style={{ borderRadius: 2 }}>
              {inboxTesting ? "Testing…" : "Test connection"}
            </Button>
            <Button onClick={checkInboxNow} disabled={checkingInbox || !s.imap_enabled} data-testid="imap-check-now" style={{ borderRadius: 2 }}>
              {checkingInbox ? "Checking…" : "Check inbox now"}
            </Button>
          </div>
          {s.imap_last_check_at && (
            <div className="mt-3 text-xs text-muted-foreground">
              Last check: {new Date(s.imap_last_check_at).toLocaleString("en-AU")} · status: <span className={s.imap_last_status === "ok" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>{s.imap_last_status || "—"}</span>
              {s.imap_last_error && <span className="text-destructive"> · {s.imap_last_error}</span>}
            </div>
          )}
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Lead sourcing</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Apollo.io</div>
          <p className="text-sm text-muted-foreground mb-4">
            Paste your Apollo API key to unlock the in-app <strong className="text-foreground">Lead Finder</strong> — search live for CFOs by title, seniority, location, employee band, and import into your pipeline. Get your key at <a className="underline" href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer">apollo.io → Settings → Integrations → API</a>. You can add this later — the rest of the engine works without it.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="overline">Apollo API key</Label>
              <Input
                type="password"
                value={s.apollo_api_key || ""}
                onChange={(e) => setS({ ...s, apollo_api_key: e.target.value })}
                placeholder="apollo_live_…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-apollo-key"
              />
            </div>
            <Button variant="outline" onClick={testApollo} disabled={apolloTesting || !apolloStatus.connected} data-testid="settings-apollo-test" style={{ borderRadius: 2 }}>
              {apolloTesting ? "Testing…" : "Test key"}
            </Button>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Status: <span className={apolloStatus.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>{apolloStatus.connected ? "connected" : "not connected"}</span> · Save first, then Test.
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-lusha-card">
          <div className="overline mb-1">Lead sourcing · alt.</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Lusha (ANZ specialist)</div>
          <p className="text-sm text-muted-foreground mb-4">
            Lusha is best-in-class for <strong className="text-foreground">Australian + NZ</strong> CFO data — better mobile-phone coverage than Apollo (~60% of AU records have direct dials). Get your key at <a className="underline" href="https://dashboard.lusha.com/integrations/api" target="_blank" rel="noreferrer">dashboard.lusha.com → Integrations → API</a> (Premium plan or above needed for prospecting endpoints). <strong className="text-foreground">Cost model</strong>: search is free, reveal costs 1 credit/email + 10 credits/phone.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="overline">Lusha API key</Label>
              <Input
                type="password"
                value={s.lusha_api_key || ""}
                onChange={(e) => setS({ ...s, lusha_api_key: e.target.value })}
                placeholder="lusha_…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-lusha-key"
              />
            </div>
            <Button variant="outline" onClick={testLusha} disabled={lushaTesting || !lushaStatus.connected} data-testid="settings-lusha-test" style={{ borderRadius: 2 }}>
              {lushaTesting ? "Testing…" : "Test key"}
            </Button>
          </div>
          <div className="mt-3 text-xs text-muted-foreground flex flex-wrap items-center gap-3">
            <span>
              Status: <span className={lushaStatus.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"} data-testid="settings-lusha-status">{lushaStatus.connected ? "connected" : "not connected"}</span>
            </span>
            {lushaInfo && (
              <span>Credits left: <span className="font-mono text-foreground" data-testid="settings-lusha-credits">{lushaInfo.remaining ?? "—"}</span> · Plan: <span className="font-mono">{lushaInfo.plan ?? "—"}</span></span>
            )}
            <span>Save first, then Test.</span>
          </div>
        </div>

        <ApifyCard s={s} setS={setS} />

        <TestLeadsCard />

        <AutoPromoteCard s={s} setS={setS} />

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Cold-email outreach</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Instantly.ai</div>
          <p className="text-sm text-muted-foreground mb-4">
            On the <strong className="text-foreground">Hyper Growth plan</strong> you can run multi-domain warmed sending and receive reply webhooks. Get an API V2 key at <a className="underline" href="https://app.instantly.ai/app/settings/integrations" target="_blank" rel="noreferrer">app.instantly.ai → Settings → Integrations</a> (V1 keys are deprecated). You can add this later.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Instantly API V2 key</Label>
              <Input
                type="password"
                value={s.instantly_api_key || ""}
                onChange={(e) => setS({ ...s, instantly_api_key: e.target.value })}
                placeholder="ins_v2_…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-instantly-key"
              />
            </div>
            <div>
              <Label className="overline">Default campaign id</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={s.instantly_default_campaign_id || ""}
                  onChange={(e) => setS({ ...s, instantly_default_campaign_id: e.target.value })}
                  placeholder="campaign_…"
                  className="input-crisp font-mono"
                  data-testid="settings-instantly-campaign"
                />
                <Button variant="outline" onClick={loadInstantlyCampaigns} disabled={loadingCampaigns || !instantlyStatus.connected} data-testid="settings-instantly-load-campaigns" style={{ borderRadius: 2 }}>
                  {loadingCampaigns ? "…" : "Load"}
                </Button>
              </div>
              {campaigns.length > 0 && (
                <Select value={s.instantly_default_campaign_id || ""} onValueChange={(v) => setS({ ...s, instantly_default_campaign_id: v })}>
                  <SelectTrigger className="input-crisp text-xs mt-2" data-testid="settings-instantly-campaign-select"><SelectValue placeholder="Pick a campaign" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.status ? `· ${c.status}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="overline">Webhook signing secret (optional but recommended)</Label>
              <Input
                type="password"
                value={s.instantly_webhook_secret || ""}
                onChange={(e) => setS({ ...s, instantly_webhook_secret: e.target.value })}
                placeholder="whsec_…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-instantly-webhook-secret"
              />
            </div>
            <div>
              <Label className="overline">Sender provider</Label>
              <Select value={s.sender_provider || "resend"} onValueChange={(v) => setS({ ...s, sender_provider: v })}>
                <SelectTrigger className="mt-1 input-crisp" data-testid="settings-sender-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resend">Resend (single-domain)</SelectItem>
                  <SelectItem value="instantly">Instantly (multi-domain warmed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <div className="overline mb-2">Webhook URL — paste this into Instantly</div>
            <div className="flex gap-2">
              <Input value={webhookUrl} readOnly className="input-crisp font-mono text-xs" data-testid="settings-instantly-webhook-url" />
              <Button variant="outline" onClick={copyWebhook} data-testid="settings-instantly-webhook-copy" style={{ borderRadius: 2 }}>Copy</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              In Instantly: Settings → Integrations → Webhooks. Subscribe to <code className="font-mono">reply_received</code>, <code className="font-mono">email_sent</code>, <code className="font-mono">email_opened</code>, <code className="font-mono">email_link_clicked</code>, <code className="font-mono">email_bounced</code>, <code className="font-mono">lead_unsubscribed</code>. Replies auto-pause sequences and feed the Qualification queue.
            </p>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={testInstantly} disabled={instantlyTesting || !instantlyStatus.connected} data-testid="settings-instantly-test" style={{ borderRadius: 2 }}>
              {instantlyTesting ? "Testing…" : "Test key"}
            </Button>
            <div className="text-xs text-muted-foreground">
              Status: <span className={instantlyStatus.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>{instantlyStatus.connected ? "connected" : "not connected"}</span>
              {" · "}Webhook secret: <span className={instantlyStatus.webhook_secret_set ? "text-[hsl(var(--success))]" : "text-muted-foreground"}>{instantlyStatus.webhook_secret_set ? "set" : "not set"}</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Trigger-event detection</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>News & signal scanning</div>
          <p className="text-sm text-muted-foreground mb-4">
            Daily news/SEC/RSS scan per active lead at 02:00 UTC. AI ranks every headline as a CFO outreach trigger (high/med/low) with a 1-line angle. Fallback chain: <strong className="text-foreground">Perplexity Sonar → Tavily → free Google News</strong>. The free fallback works without any keys — paid keys give better recall and freshness.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Perplexity API key (optional, primary)</Label>
              <Input
                type="password"
                value={s.perplexity_api_key || ""}
                onChange={(e) => setS({ ...s, perplexity_api_key: e.target.value })}
                placeholder="pplx-…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-perplexity-key"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Get key at <a className="underline" href="https://www.perplexity.ai/settings/api" target="_blank" rel="noreferrer">perplexity.ai/settings/api</a>.
              </div>
            </div>
            <div>
              <Label className="overline">Tavily API key (optional, secondary)</Label>
              <Input
                type="password"
                value={s.tavily_api_key || ""}
                onChange={(e) => setS({ ...s, tavily_api_key: e.target.value })}
                placeholder="tvly-…"
                className="mt-1 input-crisp font-mono"
                data-testid="settings-tavily-key"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Get key at <a className="underline" href="https://app.tavily.com" target="_blank" rel="noreferrer">app.tavily.com</a>.
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
            <Switch checked={s.triggers_enabled !== false} onCheckedChange={(v) => setS({ ...s, triggers_enabled: v })} data-testid="settings-triggers-enabled" />
            <div className="text-sm">Auto-scan active leads daily at 02:00 UTC</div>
          </div>
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Daily action target (Sales Coach quota)</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={s.daily_action_target ?? 8}
                onChange={(e) => setS({ ...s, daily_action_target: Number(e.target.value) || 8 })}
                className="mt-1 input-crisp font-mono w-32"
                data-testid="settings-daily-action-target"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Coach progress ring fills as you complete actions. Tip: 8/day = ~10 booked meetings/week at typical conversion.
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Click-to-call · Phase 2</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Twilio Voice</div>
          <p className="text-sm text-muted-foreground mb-4">
            Click "Call" on any lead → Twilio dials your phone first, bridges to the lead, records both legs, then AI transcribes & summarises with next-step recommendations. Get keys at <a className="underline" href="https://console.twilio.com" target="_blank" rel="noreferrer">console.twilio.com</a>. All keys optional — Call button will show "Not connected" until set.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Account SID</Label>
              <Input type="password" value={s.twilio_account_sid || ""} onChange={(e) => setS({ ...s, twilio_account_sid: e.target.value })} placeholder="AC…" className="mt-1 input-crisp font-mono" data-testid="settings-twilio-sid" />
            </div>
            <div>
              <Label className="overline">Auth Token</Label>
              <Input type="password" value={s.twilio_auth_token || ""} onChange={(e) => setS({ ...s, twilio_auth_token: e.target.value })} placeholder="…" className="mt-1 input-crisp font-mono" data-testid="settings-twilio-token" />
            </div>
            <div>
              <Label className="overline">Twilio phone number (your sender)</Label>
              <Input value={s.twilio_phone_number || ""} onChange={(e) => setS({ ...s, twilio_phone_number: e.target.value })} placeholder="+61400000000" className="mt-1 input-crisp font-mono" data-testid="settings-twilio-from" />
            </div>
            <div>
              <Label className="overline">Your phone (Twilio rings you first)</Label>
              <Input value={s.twilio_user_phone || ""} onChange={(e) => setS({ ...s, twilio_user_phone: e.target.value })} placeholder="+61400000000" className="mt-1 input-crisp font-mono" data-testid="settings-twilio-user-phone" />
            </div>
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            All recordings go through Twilio (your account). Whisper transcribes via Emergent LLM key; Claude summarises and proposes next steps. No call audio leaves your stack.
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">AI voicemail drops · ElevenLabs</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Realistic voicemail synthesis</div>
          <p className="text-sm text-muted-foreground mb-4">
            Auto-drop personalised AI voicemails per lead — Claude writes the script, ElevenLabs synthesises your chosen voice, Twilio dials with answering-machine-detection so the message lands in voicemail. Triggered automatically by the <strong className="text-foreground">voicemail</strong> sequence step. Get key at <a className="underline" href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer">elevenlabs.io → API keys</a>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">ElevenLabs API key</Label>
              <Input type="password" value={s.elevenlabs_api_key || ""} onChange={(e) => setS({ ...s, elevenlabs_api_key: e.target.value })} placeholder="sk_…" className="mt-1 input-crisp font-mono" data-testid="settings-elevenlabs-key" />
            </div>
            <div>
              <Label className="overline">Voice</Label>
              <div className="flex gap-2 mt-1">
                <Input value={s.elevenlabs_voice_id || ""} onChange={(e) => setS({ ...s, elevenlabs_voice_id: e.target.value })} placeholder="21m00Tcm4Tlm (Rachel)" className="input-crisp font-mono" data-testid="settings-elevenlabs-voice-id" />
                <Button variant="outline" onClick={loadVoices} data-testid="settings-elevenlabs-load-voices" style={{ borderRadius: 2 }}>Load</Button>
              </div>
              {voiceList.length > 0 && (
                <Select value={s.elevenlabs_voice_id || ""} onValueChange={(v) => setS({ ...s, elevenlabs_voice_id: v })}>
                  <SelectTrigger className="input-crisp text-xs mt-2" data-testid="settings-elevenlabs-voice-select"><SelectValue placeholder="Pick a voice" /></SelectTrigger>
                  <SelectContent>
                    {voiceList.map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name} {v.category ? `· ${v.category}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={previewVoicemail} disabled={previewLoading || !s.elevenlabs_api_key} data-testid="settings-elevenlabs-preview" style={{ borderRadius: 2 }}>
              {previewLoading ? "Generating…" : "Preview voicemail"}
            </Button>
            <span className="text-xs text-muted-foreground">Uses your first lead as the test target — you'll hear what gets dropped.</span>
          </div>
          {previewAudio && (
            <div className="mt-4 bg-secondary/40 p-3" style={{ borderRadius: 2 }}>
              <div className="overline mb-1">Preview for {previewAudio.lead?.full_name} ({previewAudio.lead?.company})</div>
              <div className="text-sm border-l-2 border-primary pl-3 italic mb-2">"{previewAudio.script}"</div>
              <audio controls src={previewAudio.url} className="w-full max-w-md" data-testid="settings-elevenlabs-audio" />
            </div>
          )}
        </div>

        <RunMyDayCard s={s} setS={setS} />
        <BriefingCard s={s} setS={setS} />
        <ActionListCard s={s} setS={setS} />
        <ReactivationCard s={s} setS={setS} />
        <BackupCard />

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Live AI cold-calling · Vapi.ai &amp; Bland.ai</div>
          <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Autonomous 2-way AI calls</div>
          <p className="text-sm text-muted-foreground mb-4">
            Click <strong className="text-foreground">AI Call</strong> on any lead → an AI agent dials the lead, runs a 90-second discovery, books a meeting (or politely closes), and posts the transcript + summary into the lead's activity timeline. Pick your provider and paste the matching keys. Get a Vapi key at <a className="underline" href="https://dashboard.vapi.ai" target="_blank" rel="noreferrer">dashboard.vapi.ai</a>; Bland at <a className="underline" href="https://app.bland.ai/dashboard" target="_blank" rel="noreferrer">app.bland.ai</a>. Both are optional — the call button stays disabled until at least one is connected.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Active provider</Label>
              <Select value={s.ai_call_provider || "vapi"} onValueChange={(v) => setS({ ...s, ai_call_provider: v })}>
                <SelectTrigger className="mt-1 input-crisp" data-testid="settings-ai-call-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vapi">Vapi.ai (OpenAI + ElevenLabs voice)</SelectItem>
                  <SelectItem value="bland">Bland.ai (single-call API)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">
                The button on each lead dispatches via this provider. You can switch any time.
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <div className="overline mb-2">Vapi.ai</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Vapi API key</Label>
                <Input
                  type="password"
                  value={s.vapi_api_key || ""}
                  onChange={(e) => setS({ ...s, vapi_api_key: e.target.value })}
                  placeholder="vapi_…"
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-vapi-key"
                />
              </div>
              <div>
                <Label className="overline">Vapi phone number ID</Label>
                <Input
                  value={s.vapi_phone_number_id || ""}
                  onChange={(e) => setS({ ...s, vapi_phone_number_id: e.target.value })}
                  placeholder="pn_…"
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-vapi-phone-id"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Vapi → Phone Numbers → copy the ID of the number you want to dial from.
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="overline">Webhook signing secret (optional)</Label>
                <Input
                  type="password"
                  value={s.vapi_webhook_secret || ""}
                  onChange={(e) => setS({ ...s, vapi_webhook_secret: e.target.value })}
                  placeholder="whsec_…"
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-vapi-webhook-secret"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <div className="overline mb-2">Bland.ai</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Bland API key</Label>
                <Input
                  type="password"
                  value={s.bland_api_key || ""}
                  onChange={(e) => setS({ ...s, bland_api_key: e.target.value })}
                  placeholder="org_…"
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-bland-key"
                />
              </div>
              <div>
                <Label className="overline">Voice (e.g. maya, june)</Label>
                <Input
                  value={s.bland_voice_id || ""}
                  onChange={(e) => setS({ ...s, bland_voice_id: e.target.value })}
                  placeholder="maya"
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-bland-voice"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-4">
            <div>
              Vapi: <span className={aiCallStatus.vapi_connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"} data-testid="settings-vapi-status">{aiCallStatus.vapi_connected ? "connected" : "not connected"}</span>
            </div>
            <div>
              Bland: <span className={aiCallStatus.bland_connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"} data-testid="settings-bland-status">{aiCallStatus.bland_connected ? "connected" : "not connected"}</span>
            </div>
            <div>Active: <span className="font-mono">{aiCallStatus.ai_call_provider || s.ai_call_provider || "vapi"}</span></div>
          </div>

          {/* ────────────────────────── Proactive cold-calling ────────────────────────── */}
          <div className="mt-5 pt-5 border-t border-border">
            <div className="overline mb-1">Proactive cold calling</div>
            <p className="text-sm text-muted-foreground mb-3">
              Picks <strong className="text-foreground">warm CFOs</strong> (≥ N LinkedIn touches + has phone) and queues one Bland.ai call per 20-min tick inside the call window (10am-4pm Melbourne weekdays). 14-day cooldown per lead. Capped daily so Bland credits don't blow.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-3">
                <Switch
                  id="ai-call-proactive"
                  data-testid="settings-ai-call-proactive-toggle"
                  checked={!!s.ai_call_proactive_enabled}
                  onCheckedChange={(v) => setS({ ...s, ai_call_proactive_enabled: !!v })}
                />
                <Label htmlFor="ai-call-proactive" className="cursor-pointer">
                  {s.ai_call_proactive_enabled ? "ON — will dial automatically" : "OFF — reactive only"}
                </Label>
              </div>
              <div>
                <Label className="overline">Daily cap</Label>
                <Input
                  type="number" min={1} max={20}
                  value={s.ai_call_proactive_daily_cap ?? 5}
                  onChange={(e) => setS({ ...s, ai_call_proactive_daily_cap: Math.max(1, Math.min(20, Number(e.target.value) || 5)) })}
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-ai-call-proactive-cap"
                />
              </div>
              <div>
                <Label className="overline">Min LinkedIn touches</Label>
                <Input
                  type="number" min={1} max={10}
                  value={s.ai_call_proactive_min_touches ?? 2}
                  onChange={(e) => setS({ ...s, ai_call_proactive_min_touches: Math.max(1, Math.min(10, Number(e.target.value) || 2)) })}
                  className="mt-1 input-crisp font-mono"
                  data-testid="settings-ai-call-proactive-min-touches"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              A lead is eligible when: phone number present · ≥ {s.ai_call_proactive_min_touches ?? 2} LinkedIn touches · not unsubscribed/engaged/won · no AI call in last 14 days.
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }}>
          <div className="overline mb-1">Integrations</div>
          <div className="font-serif text-xl tracking-tight mb-3" style={{ fontWeight: 450 }}>Status</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3 text-sm">
            <Status name="Emergent Universal LLM" ok hint="Live" />
            <Status name="Resend (email)" ok={false} hint="Add RESEND_API_KEY in backend/.env to enable real sending. Currently simulated." />
            <Status name="Apollo (lead sourcing)" ok={apolloStatus.connected} hint={apolloStatus.connected ? "Connected" : "Add API key above"} />
            <Status name="Instantly (cold email)" ok={instantlyStatus.connected} hint={instantlyStatus.connected ? `Connected${instantlyStatus.default_campaign_id ? " · default campaign set" : ""}` : "Add API V2 key above"} />
            <Status name="Triggers (news/AI)" ok hint={s.perplexity_api_key ? "Perplexity primary" : s.tavily_api_key ? "Tavily primary" : "Free Google News fallback active"} />
            <Status name="Twilio (calls/voicemail)" ok={!!s.twilio_account_sid && !!s.twilio_auth_token} hint={s.twilio_account_sid ? "Connected" : "Add SID + token above"} />
            <Status name="ElevenLabs (voicemail TTS)" ok={!!s.elevenlabs_api_key} hint={s.elevenlabs_api_key ? "Connected" : "Add API key above"} />
            <Status name="Vapi (AI calls)" ok={!!aiCallStatus.vapi_connected} hint={aiCallStatus.vapi_connected ? "Connected" : "Add Vapi key + phone number id above"} />
            <Status name="Bland (AI calls)" ok={!!aiCallStatus.bland_connected} hint={aiCallStatus.bland_connected ? "Connected" : "Add Bland API key above"} />
            <Status name="Outlook / Calendly" ok={!!s.booking_link} hint={s.booking_link ? "Booking link set" : "Paste your booking link above"} />
            <Status name="Inbox Watcher (IMAP)" ok={!!s.imap_enabled && s.imap_last_status === "ok"} hint={!s.imap_enabled ? "Disabled — enable in Inbox Watcher card above" : (s.imap_last_status === "ok" ? "Connected" : "Not yet checked or error")} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ApifyCard({ s, setS }) {
  const [status, setStatus] = useState({ connected: false });
  const [testing, setTesting] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/apify/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [s.apify_api_token]);

  const testKey = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/apify/test");
      if (data.ok) {
        setInfo({ user: data.user, actor: data.actor_name });
        toast.success(`Apify: connected as ${data.user || "—"} · actor: ${data.actor_name || "—"}`);
      } else {
        toast.error(`Apify: ${data.error}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apify test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-apify-card">
      <div className="overline mb-1">Lead sourcing · LinkedIn Sales Navigator (automated)</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Apify · Sales Nav scrape</div>
      <p className="text-sm text-muted-foreground mb-4">
        Use <strong className="text-foreground">Lead Finder → Filter builder</strong> to pick titles, locations, company sizes and seniority. We scrape matching CFO profiles via Apify (no LinkedIn cookie required), enrich each row with Lusha for verified email + phone, and auto-enroll every imported lead into your 11-step CFO sequence. Get an API token at <a className="underline" href="https://console.apify.com/account/integrations" target="_blank" rel="noreferrer">console.apify.com → Settings → Integrations → API tokens</a>. Default actor is <span className="font-mono text-foreground">harvestapi/linkedin-profile-search</span> (~$4.10 per 1,000 full profiles, no cookies). You can swap actors below.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label className="overline">Apify API token</Label>
          <Input
            type="password"
            value={s.apify_api_token || ""}
            onChange={(e) => setS({ ...s, apify_api_token: e.target.value })}
            placeholder="apify_api_…"
            className="mt-1 input-crisp font-mono"
            data-testid="settings-apify-token"
          />
        </div>
        <Button variant="outline" onClick={testKey} disabled={testing || !status.connected} data-testid="settings-apify-test" style={{ borderRadius: 2 }}>
          {testing ? "Testing…" : "Test token"}
        </Button>
      </div>
      <div className="mt-3">
        <Label className="overline">Actor ID (defaults to harvestapi / no-cookies)</Label>
        <Input
          value={s.apify_actor_id || ""}
          onChange={(e) => setS({ ...s, apify_actor_id: e.target.value })}
          placeholder="harvestapi/linkedin-profile-search"
          className="mt-1 input-crisp font-mono text-xs"
          data-testid="settings-apify-actor-id"
        />
        <div className="text-xs text-muted-foreground mt-1">
          Use either <span className="font-mono">username/actor-name</span> or <span className="font-mono">username~actor-name</span>. Recommended: <span className="font-mono">harvestapi/linkedin-profile-search</span> (no cookies, structured filters, $4.10/1K profiles).
        </div>
      </div>
      <div className="mt-3 text-xs text-muted-foreground flex flex-wrap items-center gap-3">
        <span>
          Status: <span className={status.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"} data-testid="settings-apify-status">{status.connected ? "connected" : "not connected"}</span>
        </span>
        {info && (
          <span>User: <span className="font-mono text-foreground">{info.user || "—"}</span> · Actor: <span className="font-mono">{info.actor || "—"}</span></span>
        )}
        <span>Save first, then Test.</span>
      </div>
    </div>
  );
}


function Status({ name, ok, hint }) {
  return (
    <div className="border border-border p-3" style={{ borderRadius: 2 }} data-testid={`integration-status-${name.replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2">
        <div className={`h-2 w-2 ${ok ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} style={{ borderRadius: 999 }} />
        <div className="font-medium">{name}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

function LandingPageCard() {
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api
      .get("/site/my-config")
      .then((r) => setCfg(r.data.config || {}))
      .catch(() => setCfg({}))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.slug || !cfg.display_name) {
      toast.error("Slug + display name are required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/site/my-config", cfg);
      toast.success("Landing page saved — preview at /site/" + cfg.slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const cases = cfg.case_studies || [];
  const setCase = (i, patch) => {
    const next = [...cases];
    next[i] = { ...(next[i] || {}), ...patch };
    setField("case_studies", next);
  };

  return (
    <div
      className="border border-border p-4 space-y-3"
      style={{ borderRadius: 2 }}
      data-testid="settings-landing-card"
    >
      <div>
        <div className="overline mb-1">Personal landing page</div>
        <div className="font-medium text-sm mb-1">
          Public brand hub for CFO / CXO / board-advisory enquiries
        </div>
        <p className="text-xs text-muted-foreground">
          Set your slug + bio and your page goes live at <span className="font-mono">/site/[slug]</span>.
          Newsletters + LinkedIn posts auto-sync. Email captures land in the Inbox.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="overline">Slug (URL)</Label>
          <Input
            value={cfg.slug || ""}
            placeholder="tarang-gopani"
            onChange={(e) => setField("slug", e.target.value.toLowerCase())}
            className="mt-1 font-mono input-crisp"
            data-testid="landing-slug"
          />
        </div>
        <div>
          <Label className="overline">Display name</Label>
          <Input
            value={cfg.display_name || ""}
            placeholder="Tarang Gopani"
            onChange={(e) => setField("display_name", e.target.value)}
            className="mt-1 input-crisp"
            data-testid="landing-name"
          />
        </div>
      </div>

      <div>
        <Label className="overline">Headline positioning (1 line)</Label>
        <Input
          value={cfg.headline || ""}
          placeholder="I help ASX CFOs cut 15-30% out of indirect spend without touching headcount."
          onChange={(e) => setField("headline", e.target.value)}
          className="mt-1 input-crisp"
          data-testid="landing-headline"
        />
      </div>

      <div>
        <Label className="overline">Bio (2-4 paragraphs)</Label>
        <Textarea
          rows={5}
          value={cfg.bio || ""}
          placeholder="A short story about who you are, what you've done, and why CFOs / boards should call you."
          onChange={(e) => setField("bio", e.target.value)}
          className="mt-1 text-sm"
          data-testid="landing-bio"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="overline">Photo URL</Label>
          <Input
            value={cfg.photo_url || ""}
            placeholder="https://..."
            onChange={(e) => setField("photo_url", e.target.value)}
            className="mt-1 input-crisp"
            data-testid="landing-photo-url"
          />
        </div>
        <div>
          <Label className="overline">Calendar (Calendly / Cal.com)</Label>
          <Input
            value={cfg.calendar_url || ""}
            placeholder="https://cal.com/tarang"
            onChange={(e) => setField("calendar_url", e.target.value)}
            className="mt-1 input-crisp"
            data-testid="landing-cal"
          />
        </div>
        <div>
          <Label className="overline">ERA Group profile URL</Label>
          <Input
            value={cfg.era_group_url || ""}
            placeholder="https://eragroup.com/..."
            onChange={(e) => setField("era_group_url", e.target.value)}
            className="mt-1 input-crisp"
            data-testid="landing-era"
          />
        </div>
        <div>
          <Label className="overline">LinkedIn URL</Label>
          <Input
            value={cfg.linkedin_url || ""}
            placeholder="https://linkedin.com/in/tarang-gopani"
            onChange={(e) => setField("linkedin_url", e.target.value)}
            className="mt-1 input-crisp"
            data-testid="landing-li"
          />
        </div>
      </div>

      <div>
        <Label className="overline">Case studies (3 recommended)</Label>
        <div className="space-y-2 mt-1">
          {[0, 1, 2].map((i) => {
            const cs = cases[i] || {};
            return (
              <div
                key={i}
                className="grid grid-cols-[100px_140px_1fr] gap-2"
                data-testid={`landing-case-${i}`}
              >
                <Input
                  placeholder="Result"
                  value={cs.result || ""}
                  onChange={(e) => setCase(i, { result: e.target.value })}
                  className="input-crisp text-sm"
                />
                <Input
                  placeholder="Industry"
                  value={cs.industry || ""}
                  onChange={(e) => setCase(i, { industry: e.target.value })}
                  className="input-crisp text-sm"
                />
                <Input
                  placeholder="One-line summary of what you did + outcome"
                  value={cs.summary || ""}
                  onChange={(e) => setCase(i, { summary: e.target.value })}
                  className="input-crisp text-sm"
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button onClick={save} disabled={saving} data-testid="landing-save">
          {saving ? "Saving…" : "Save landing page"}
        </Button>
        {cfg.slug && (
          <a
            href={`/site/${cfg.slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline"
            data-testid="landing-preview"
          >
            Preview live page →
          </a>
        )}
      </div>
    </div>
  );
}


function ResetLinkedInCard() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const handleReset = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/linkedin-auto/reset-linkedin");
      setResult(data);
      toast.success(
        `LinkedIn state reset — cleared ${data.cleared.inmail_leads_cleared} InMail flags, ${data.cleared.invite_backoffs_cleared} invite backoffs.`,
      );
      setShowConfirm(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-amber-300 bg-amber-50/60 p-4"
      style={{ borderRadius: 2 }}
      data-testid="settings-reset-linkedin-card"
    >
      <div className="overline mb-1 text-amber-900">Recovery · one-click</div>
      <div className="font-medium text-sm text-amber-950 mb-2">
        LinkedIn sends stuck at 0/25? Force reset + reconnect
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Use when the daily digest shows invites/DMs 0/25 with high eligible counts
        for &gt;12 hours despite a fresh scheduler tick. Clears every auto-pause
        flag (account throttle timer, InMail-disabled, per-lead unavailability +
        future invite back-offs) and immediately opens the Unipile reconnect flow
        so you can re-link Sales Navigator in one hop. Historical LinkedIn dedupe
        windows are preserved so we don't spam.
      </p>
      {!showConfirm ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowConfirm(true)}
          data-testid="settings-reset-linkedin-btn"
        >
          Force reset LinkedIn state
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleReset}
            disabled={busy}
            data-testid="settings-reset-linkedin-confirm"
          >
            {busy ? "Resetting…" : "Yes, reset + open reconnect"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowConfirm(false)}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
      )}
      {result && (
        <div className="mt-3 p-3 bg-white border border-amber-200 text-xs" data-testid="settings-reset-linkedin-result">
          <div className="font-medium mb-1">Reset applied · {new Date(result.reset_at).toLocaleString()}</div>
          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
            <li>Settings flags cleared (throttle timer + InMail disable)</li>
            <li>{result.cleared.inmail_leads_cleared} lead(s) had per-lead InMail flags cleared</li>
            <li>{result.cleared.invite_backoffs_cleared} future invite back-offs cleared</li>
            <li>Scheduler: {result.kick || "not kicked"}</li>
          </ul>
          {result.hosted_auth_url && (
            <a
              href={result.hosted_auth_url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-primary underline font-medium"
              data-testid="settings-reset-linkedin-reconnect"
            >
              Open Unipile reconnect →
            </a>
          )}
        </div>
      )}
    </div>
  );
}


function AutoPromoteCard({ s, setS }) {
  const enabled = s.auto_promote_linkedin_only === true;
  return (
    <div
      className="bg-card border border-border p-6 lg:col-span-2"
      style={{ borderRadius: 2 }}
      data-testid="settings-auto-promote-card"
    >
      <div className="overline mb-1">Optional · Auto-promote LinkedIn replies to email funnel</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>
        Escalate warm LinkedIn replies to email + voicemail
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        <strong className="text-foreground">Off by default — LinkedIn automation does not require this.</strong> When ON, a positive LinkedIn DM reply triggers Claude to classify sentiment, calls Lusha to reveal the CFO's email + phone, and enrols them in the multi-channel email + voicemail sequence. Lusha credits are only spent on already-engaged leads. Leave OFF if LinkedIn is your only channel.
      </p>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {enabled ? (
            s.lusha_api_key
              ? <span className="text-[hsl(var(--success))]">Active · ready to upgrade warm replies</span>
              : <span className="text-[hsl(var(--warning))]">Add a Lusha key above to use this feature</span>
          ) : (
            <span>Disabled — LinkedIn replies stay in the LinkedIn channel</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            onCheckedChange={(v) => setS({ ...s, auto_promote_linkedin_only: v })}
            data-testid="settings-auto-promote-toggle"
          />
          <Label className="text-sm">
            {enabled ? "Enabled" : "Disabled"}
          </Label>
        </div>
      </div>
    </div>
  );
}

function TestLeadsCard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [autoEnroll, setAutoEnroll] = useState(false);
  const [rows, setRows] = useState([
    { email: "", full_name: "", company: "" },
    { email: "", full_name: "", company: "" },
    { email: "", full_name: "", company: "" },
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/test-leads");
      setList(data.leads || []);
    } catch {
      // soft fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (i, key, val) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };

  const addRow = () => {
    if (rows.length >= 10) return;
    setRows((rs) => [...rs, { email: "", full_name: "", company: "" }]);
  };

  const removeRow = (i) => {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    const payloadLeads = rows
      .map((r) => ({
        email: (r.email || "").trim(),
        full_name: (r.full_name || "").trim() || undefined,
        company: (r.company || "").trim() || undefined,
      }))
      .filter((r) => r.email);
    if (!payloadLeads.length) {
      toast.error("Add at least one email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/test-leads/bulk-add", {
        leads: payloadLeads,
        auto_enroll: autoEnroll,
      });
      const msg = [];
      if (data.created) msg.push(`${data.created} created`);
      if (data.skipped) msg.push(`${data.skipped} already-existed (skipped)`);
      if (data.errors) msg.push(`${data.errors} errored`);
      toast.success(msg.join(" · ") || "Done");
      // reset rows to defaults
      setRows([
        { email: "", full_name: "", company: "" },
        { email: "", full_name: "", company: "" },
        { email: "", full_name: "", company: "" },
      ]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not add test leads");
    } finally {
      setBusy(false);
    }
  };

  const wipeAll = async () => {
    if (!confirm(`Delete ALL ${list.length} test leads + their enrollments + drafts? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/test-leads/all");
      toast.success(`Wiped ${data.deleted_leads} test leads (${data.deleted_enrollments} enrollments cleared)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (lead) => {
    if (!confirm(`Delete test lead ${lead.email}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/test-leads/${lead.lead_id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-card border border-border p-6 lg:col-span-2"
      style={{ borderRadius: 2 }}
      data-testid="settings-test-leads-card"
    >
      <div className="overline mb-1">Test Leads · safe end-to-end</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>
        Pipeline dry-run with your own emails
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Add up to 10 of your own (or friends') email addresses to validate the full
        <strong className="text-foreground"> Research → Draft → Send → Reply </strong>
        pipeline without sending to real CFO leads — bounced emails on real prospects
        would tank your Instantly warmup score. Test leads are tagged
        <code className="text-xs mx-1 px-1 bg-muted">test_lead</code>
        and can be wiped in one click.
      </p>

      <div className="space-y-2 mb-4">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`tl-row-${i}`}>
            <Input
              placeholder="you@yourdomain.com"
              value={r.email}
              onChange={(e) => updateRow(i, "email", e.target.value)}
              className="md:col-span-5 input-crisp"
              data-testid={`tl-row-email-${i}`}
            />
            <Input
              placeholder="Full name (optional)"
              value={r.full_name}
              onChange={(e) => updateRow(i, "full_name", e.target.value)}
              className="md:col-span-3 input-crisp"
              data-testid={`tl-row-name-${i}`}
            />
            <Input
              placeholder="Company (optional)"
              value={r.company}
              onChange={(e) => updateRow(i, "company", e.target.value)}
              className="md:col-span-3 input-crisp"
              data-testid={`tl-row-company-${i}`}
            />
            <button
              onClick={() => removeRow(i)}
              className="md:col-span-1 text-xs text-muted-foreground hover:text-destructive underline"
              data-testid={`tl-row-remove-${i}`}
            >
              remove
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <button
          onClick={addRow}
          disabled={rows.length >= 10}
          className="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-40"
          data-testid="tl-add-row"
        >
          + add another row
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <Switch
            checked={autoEnroll}
            onCheckedChange={setAutoEnroll}
            data-testid="tl-auto-enroll"
          />
          <span className="text-xs text-muted-foreground">
            Auto-enroll into default sequence (drafts start immediately)
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={submit}
          disabled={busy}
          style={{ borderRadius: 2 }}
          data-testid="tl-submit"
        >
          {busy ? "Adding…" : "Add test leads"}
        </Button>
        {list.length > 0 && (
          <Button
            onClick={wipeAll}
            disabled={busy}
            variant="outline"
            className="text-destructive"
            style={{ borderRadius: 2 }}
            data-testid="tl-wipe"
          >
            Wipe all ({list.length})
          </Button>
        )}
      </div>

      {list.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <div className="overline mb-2">Current test leads ({list.length})</div>
          <div className="space-y-1.5" data-testid="tl-list">
            {list.map((l) => (
              <div
                key={l.lead_id}
                className="flex items-center justify-between gap-2 text-xs border border-border p-2"
                style={{ borderRadius: 2 }}
                data-testid={`tl-item-${l.lead_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-sm">{l.full_name}</strong>
                    <span className="text-[10px] px-1.5 py-0.5 bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning))]" style={{ borderRadius: 2 }}>
                      TEST
                    </span>
                    <span className="text-muted-foreground">· {l.email}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {l.company} · status: {l.status}
                  </div>
                </div>
                <button
                  onClick={() => deleteOne(l)}
                  className="text-muted-foreground hover:text-destructive underline"
                  data-testid={`tl-item-delete-${l.lead_id}`}
                >
                  remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-muted-foreground mt-2 italic">Loading…</div>}
    </div>
  );
}


const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function RunMyDayCard({ s, setS }) {
  const schedule = Array.isArray(s.auto_runner_schedule) ? s.auto_runner_schedule : [];

  const slotsByDay = (day) => schedule.filter((x) => Number(x.day) === day).map((x) => x.time).sort();

  const addSlot = (day, time) => {
    if (!time || !/^\d{2}:\d{2}$/.test(time)) return;
    const exists = schedule.some((x) => Number(x.day) === day && x.time === time);
    if (exists) return;
    const next = [...schedule, { day, time }].sort((a, b) => a.day - b.day || a.time.localeCompare(b.time));
    setS({ ...s, auto_runner_schedule: next });
  };

  const removeSlot = (day, time) => {
    const next = schedule.filter((x) => !(Number(x.day) === day && x.time === time));
    setS({ ...s, auto_runner_schedule: next });
  };

  return (
    <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-runner-card">
      <div className="overline mb-1">Run my day · automation</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>One button, one engine</div>
      <p className="text-sm text-muted-foreground mb-4">
        Auto-execute your daily Coach actions on your schedule. Trigger emails, AI voicemails, and AI cold calls fire on weekdays; weekends drop to LinkedIn-only so CFOs don't see Sunday-morning spam. Inbound replies are <strong className="text-foreground">always drafted, never auto-sent</strong> — you stay in the loop on every conversation.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border p-4 flex items-start gap-3" style={{ borderRadius: 2 }}>
          <div className="flex-1">
            <div className="font-medium mb-1">Auto-runner</div>
            <div className="text-xs text-muted-foreground">When ON, the engine fires on the schedule below in your local time. Manual <strong className="text-foreground">Run my day</strong> button always works regardless.</div>
          </div>
          <Switch checked={!!s.auto_runner_enabled} onCheckedChange={(v) => setS({ ...s, auto_runner_enabled: v })} data-testid="settings-runner-enabled" />
        </div>

        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Mode</Label>
          <Select value={s.auto_runner_mode || "draft_only"} onValueChange={(v) => setS({ ...s, auto_runner_mode: v })}>
            <SelectTrigger className="input-crisp mt-1" data-testid="settings-runner-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft_only">Draft only — review before send</SelectItem>
              <SelectItem value="autonomous">Autonomous — send emails, drop voicemails, place AI calls</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-2">Inbound replies always stay in draft mode regardless.</div>
        </div>

        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Cap mode</Label>
          <Select value={s.auto_runner_cap_mode || "soft_ceiling"} onValueChange={(v) => setS({ ...s, auto_runner_cap_mode: v })}>
            <SelectTrigger className="input-crisp mt-1" data-testid="settings-runner-cap-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="exact_cap">Exactly target ({s.daily_action_target ?? 8}/run)</SelectItem>
              <SelectItem value="soft_ceiling">Soft ceiling (1.5× target — default)</SelectItem>
              <SelectItem value="uncapped_high">Uncapped — burn through all hot actions</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-2">Tied to your daily quota target above.</div>
        </div>

        <div className="border border-border p-4 md:col-span-2 flex items-start gap-3" style={{ borderRadius: 2 }}>
          <div className="flex-1">
            <div className="font-medium mb-1">Weekend = LinkedIn only</div>
            <div className="text-xs text-muted-foreground">Skip emails and voicemails on Sat/Sun (in your timezone). LinkedIn DMs &amp; sequence_tasks still surface. Recommended ON.</div>
          </div>
          <Switch checked={s.auto_runner_skip_weekend_email !== false} onCheckedChange={(v) => setS({ ...s, auto_runner_skip_weekend_email: v })} data-testid="settings-runner-weekend-li-only" />
        </div>

        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Timezone</Label>
          <Input value={s.auto_runner_timezone || "Australia/Sydney"} onChange={(e) => setS({ ...s, auto_runner_timezone: e.target.value })} className="mt-1 input-crisp font-mono" placeholder="Australia/Sydney" data-testid="settings-runner-timezone" />
          <div className="text-xs text-muted-foreground mt-1">IANA TZ — e.g. Australia/Sydney, Europe/London, America/Los_Angeles.</div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border">
        <div className="overline mb-3">Schedule grid · pick days &amp; times</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {WEEKDAYS.map((label, day) => (
            <DaySlotPicker key={day} day={day} label={label} times={slotsByDay(day)} onAdd={addSlot} onRemove={removeSlot} />
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-3">
          Each slot fires once per day in your TZ. The dispatcher checks every 5 minutes and de-duplicates so a slot can't fire twice. Total scheduled runs: <span className="font-mono text-foreground" data-testid="settings-runner-slot-count">{schedule.length}</span>.
        </div>
      </div>
    </div>
  );
}

function DaySlotPicker({ day, label, times, onAdd, onRemove }) {
  const [val, setVal] = useState("09:00");
  return (
    <div className="border border-border p-3" style={{ borderRadius: 2 }} data-testid={`settings-runner-day-${day}`}>
      <div className="overline mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
        {times.length === 0 && <span className="text-xs text-muted-foreground/60 italic">no runs</span>}
        {times.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 bg-secondary border border-border" style={{ borderRadius: 2 }} data-testid={`settings-runner-slot-${day}-${t}`}>
            {t}
            <button onClick={() => onRemove(day, t)} className="text-muted-foreground hover:text-foreground" aria-label="remove">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <Input type="time" value={val} onChange={(e) => setVal(e.target.value)} className="input-crisp text-xs h-8" data-testid={`settings-runner-time-input-${day}`} />
        <button
          onClick={() => onAdd(day, val)}
          className="px-2 text-xs bg-foreground text-background hover:opacity-90"
          style={{ borderRadius: 2 }}
          data-testid={`settings-runner-add-${day}`}
        >+</button>
      </div>
    </div>
  );
}

function BriefingCard({ s, setS }) {
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/briefing/send-now");
      if (data.ok) toast.success(`Briefing sent (${data.via}) → ${data.recipient}`);
      else toast.error(`Send failed: ${data.error || "unknown"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/briefing/preview");
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-briefing-card">
      <div className="overline mb-1">Morning briefing · daily inbox digest</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>One-tap-from-inbox engine</div>
      <p className="text-sm text-muted-foreground mb-4">
        Each morning at your chosen time, the engine emails you a 1-paragraph recap of yesterday plus today's top-5 actions, with a single <strong className="text-foreground">⚡ Run my day</strong> magic-link button. Tap it from your phone — engine fires, drafts/sends what's queued, redirects you to the dashboard. Magic links expire in 24h.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border p-4 flex items-start gap-3" style={{ borderRadius: 2 }}>
          <div className="flex-1">
            <div className="font-medium mb-1">Auto-send</div>
            <div className="text-xs text-muted-foreground">When ON, the briefing emails on the time below in your TZ. Set TZ in the Run-my-day card above.</div>
          </div>
          <Switch checked={!!s.morning_briefing_enabled} onCheckedChange={(v) => setS({ ...s, morning_briefing_enabled: v })} data-testid="settings-briefing-enabled" />
        </div>

        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Send time (local)</Label>
          <Input
            type="time"
            value={s.morning_briefing_time || "08:00"}
            onChange={(e) => setS({ ...s, morning_briefing_time: e.target.value })}
            className="mt-1 input-crisp font-mono"
            data-testid="settings-briefing-time"
          />
          <div className="text-xs text-muted-foreground mt-1">In <span className="font-mono">{s.auto_runner_timezone || "Australia/Sydney"}</span> — change TZ in the card above.</div>
        </div>

        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Recipient (optional)</Label>
          <Input
            type="email"
            value={s.morning_briefing_recipient || ""}
            onChange={(e) => setS({ ...s, morning_briefing_recipient: e.target.value })}
            placeholder="(defaults to your account email)"
            className="mt-1 input-crisp"
            data-testid="settings-briefing-recipient"
          />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-border flex items-center gap-3 flex-wrap">
        <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="settings-briefing-send-now" style={{ borderRadius: 2 }}>
          {sending ? "Sending…" : "Send a test now"}
        </Button>
        <Button variant="outline" onClick={showPreview} disabled={previewing} data-testid="settings-briefing-preview" style={{ borderRadius: 2 }}>
          {previewing ? "Loading…" : "Preview"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Resend key not set → emails simulate (logged to <span className="font-mono">briefings</span>) until you wire <span className="font-mono">RESEND_API_KEY</span> + verified sender.
        </span>
      </div>

      {preview && (
        <div className="mt-4 border border-border" style={{ borderRadius: 2 }}>
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <div className="overline">Preview · {preview.subject}</div>
            <button onClick={() => setPreview(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="settings-briefing-preview-close">close</button>
          </div>
          <iframe
            title="briefing-preview"
            srcDoc={preview.html}
            sandbox=""
            className="w-full bg-white"
            style={{ height: 460, border: 0 }}
            data-testid="settings-briefing-preview-iframe"
          />
        </div>
      )}
    </div>
  );
}

function ActionListCard({ s, setS }) {
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/action-list/send-now");
      if (data.ok) toast.success(`Action list sent (${data.via}) · ${data.action_count} action${data.action_count !== 1 ? "s" : ""}`);
      else toast.error(`Send failed: ${data.error || "unknown"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/action-list/preview");
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-action-list-card">
      <div className="overline mb-1">Daily action list · 08:30 inbox</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Top-5 pipeline moves email</div>
      <p className="text-sm text-muted-foreground mb-4">
        Separate from the morning briefing. At your chosen time (default 08:30 local), the engine emails you the 5 highest-ROI moves to make today — each with a one-tap link straight into that lead's drawer. Designed for "after my coffee, here's exactly what to do".
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border p-4 flex items-start gap-3" style={{ borderRadius: 2 }}>
          <div className="flex-1">
            <div className="font-medium mb-1">Auto-send</div>
            <div className="text-xs text-muted-foreground">Fires at the time below in your TZ.</div>
          </div>
          <Switch checked={!!s.action_list_enabled} onCheckedChange={(v) => setS({ ...s, action_list_enabled: v })} data-testid="settings-action-list-enabled" />
        </div>
        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Send time (local)</Label>
          <Input
            type="time"
            value={s.action_list_time || "08:30"}
            onChange={(e) => setS({ ...s, action_list_time: e.target.value })}
            className="mt-1 input-crisp font-mono"
            data-testid="settings-action-list-time"
          />
        </div>
        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Recipient (optional)</Label>
          <Input
            type="email"
            value={s.action_list_recipient || ""}
            onChange={(e) => setS({ ...s, action_list_recipient: e.target.value })}
            placeholder="(defaults to morning briefing recipient)"
            className="mt-1 input-crisp"
            data-testid="settings-action-list-recipient"
          />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border flex items-center gap-3 flex-wrap">
        <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="settings-action-list-send-now" style={{ borderRadius: 2 }}>
          {sending ? "Sending…" : "Send a test now"}
        </Button>
        <Button variant="outline" onClick={showPreview} disabled={previewing} data-testid="settings-action-list-preview" style={{ borderRadius: 2 }}>
          {previewing ? "Loading…" : "Preview"}
        </Button>
      </div>
      {preview && (
        <div className="mt-4 border border-border" style={{ borderRadius: 2 }}>
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <div className="overline">Preview · {preview.subject}</div>
            <button onClick={() => setPreview(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="settings-action-list-preview-close">close</button>
          </div>
          <iframe
            title="action-list-preview"
            srcDoc={preview.html}
            sandbox=""
            className="w-full bg-white"
            style={{ height: 460, border: 0 }}
            data-testid="settings-action-list-preview-iframe"
          />
        </div>
      )}
    </div>
  );
}

function ReactivationCard({ s, setS }) {
  const [running, setRunning] = useState(false);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reactivation/run-now");
      if (data.ok) toast.success(`Reactivation drafted ${data.drafted} email(s) · ${data.eligible_found} eligible`);
      else toast.error(data.reason || "Reactivation failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reactivation failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="settings-reactivation-card">
      <div className="overline mb-1">Lost-deal re-engagement · 180-day rule</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>Auto-revive dead leads</div>
      <p className="text-sm text-muted-foreground mb-4">
        Every day at 04:00 UTC the engine finds leads in <span className="font-mono">nurture</span> / <span className="font-mono">engaged</span> / <span className="font-mono">qualified</span> with no contact in the last 180 days, and drafts ONE re-engagement email referencing a fresh industry pressure. Capped at 20 per day to protect deliverability. Each lead can only be reactivated once per 180-day window.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border p-4 flex items-start gap-3" style={{ borderRadius: 2 }}>
          <div className="flex-1">
            <div className="font-medium mb-1">Auto-run daily</div>
            <div className="text-xs text-muted-foreground">Disable if you want to handle re-engagement manually.</div>
          </div>
          <Switch checked={s.reactivation_enabled !== false} onCheckedChange={(v) => setS({ ...s, reactivation_enabled: v })} data-testid="settings-reactivation-enabled" />
        </div>
        <div className="border border-border p-4" style={{ borderRadius: 2 }}>
          <Label className="overline">Manual trigger</Label>
          <div className="text-xs text-muted-foreground mb-2">Run the 180-day pass right now instead of waiting for the cron.</div>
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="settings-reactivation-run-now" style={{ borderRadius: 2 }}>
            {running ? "Running…" : "Run reactivation now"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function BackupCard() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideMd, setGuideMd] = useState("");
  const [guideLoading, setGuideLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backup/list");
      setBackups(data.backups || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load backups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runBackup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      toast.success(
        `Backup complete · ${data.leads.toLocaleString()} leads · ${data.collections} collections · ${(
          data.size_bytes / 1024 / 1024
        ).toFixed(1)} MB`,
        { duration: 6000 }
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed");
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (filename) => {
    setDownloading(filename);
    try {
      const resp = await api.get(`/admin/backup/download/${filename}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/gzip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Permanently delete ${filename}?\nThis cannot be undone.`)) return;
    setDeleting(filename);
    try {
      await api.delete(`/admin/backup/${filename}`);
      toast.success(`Deleted ${filename}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const openGuide = async () => {
    setGuideOpen(true);
    if (guideMd) return;
    setGuideLoading(true);
    try {
      const { data } = await api.get("/admin/backup/restore-guide");
      setGuideMd(data.markdown || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load restore guide");
      setGuideMd("");
    } finally {
      setGuideLoading(false);
    }
  };

  const [emailing, setEmailing] = useState(false);
  const emailGuide = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/admin/backup/restore-guide/email", {});
      toast.success(`Restore guide emailed to ${data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const onPickRestoreFile = () => fileInputRef.current?.click();

  const onRestoreFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so picking the same file twice re-fires onChange
    if (!file) return;
    if (!file.name.endsWith(".tar.gz")) {
      toast.error("File must be a .tar.gz produced by GT Engine backup");
      return;
    }
    const sizeMb = (file.size / 1024 / 1024).toFixed(1);
    const ok = window.confirm(
      `Restore from "${file.name}" (${sizeMb} MB)?\n\n` +
        "This will REPLACE every lead, enrollment, activity and API key in the current database with what's in this file.\n\n" +
        "A safety snapshot of your CURRENT data will be saved first, so you can roll back if needed.\n\n" +
        "Proceed?"
    );
    if (!ok) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("backup_file", file);
      fd.append("confirm", "RESTORE");
      const { data } = await api.post("/admin/backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min — restores of larger DBs can take a while
      });
      setRestoreResult(data);
      toast.success(
        `Restored ${data.leads_after_restore.toLocaleString()} leads · ${data.collections_after_restore} collections`,
        { duration: 8000 }
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const fmtSize = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", {
        timeZone: "Australia/Sydney",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="bg-card border border-border p-6 lg:col-span-2"
      style={{ borderRadius: 2 }}
      data-testid="settings-backup-card"
    >
      <div className="overline mb-1">Database backup · disaster recovery</div>
      <div className="font-serif text-xl tracking-tight mb-1" style={{ fontWeight: 450 }}>
        One-click MongoDB snapshot
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Take a full snapshot of every lead, enrollment, activity, API key and webhook state — download it as a single{" "}
        <code className="text-xs bg-muted px-1">.tar.gz</code> file. Before deploying to production, click <strong>Take backup now</strong>,
        then <strong>Download</strong>, save the file safely. On the prod app, come back to this same page and click <strong>Upload & restore</strong>{" "}
        — your data lands intact, in one click, no terminal needed.
      </p>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Button
          onClick={runBackup}
          disabled={running}
          data-testid="settings-backup-run"
          style={{ borderRadius: 2 }}
        >
          {running ? "Backing up…" : "Take backup now"}
        </Button>
        <Button
          variant="outline"
          onClick={onPickRestoreFile}
          disabled={restoring}
          data-testid="settings-backup-upload-restore"
          style={{ borderRadius: 2 }}
        >
          {restoring ? "Restoring…" : "Upload & restore from .tar.gz"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".tar.gz,application/gzip"
          onChange={onRestoreFileSelected}
          className="hidden"
          data-testid="settings-backup-restore-file"
        />
        <Button
          variant="outline"
          onClick={openGuide}
          data-testid="settings-backup-view-guide"
          style={{ borderRadius: 2 }}
        >
          View restore guide
        </Button>
        <span className="text-xs text-muted-foreground">
          {backups.length === 0 ? "No backups yet" : `${backups.length} backup${backups.length !== 1 ? "s" : ""} on disk`}
        </span>
      </div>

      {restoreResult && (
        <div
          className="mb-4 p-3 border border-green-600/40 bg-green-50 dark:bg-green-950/20 text-sm"
          style={{ borderRadius: 2 }}
          data-testid="settings-backup-restore-result"
        >
          <div className="font-medium mb-1">
            ✓ Restored from {restoreResult.restored_from}
          </div>
          <div className="text-xs text-muted-foreground">
            {restoreResult.leads_after_restore.toLocaleString()} leads ·{" "}
            {restoreResult.collections_after_restore} collections now in your database.
            <br />
            Safety snapshot of your old data:{" "}
            <code className="bg-muted px-1">{restoreResult.safety_snapshot}</code> —
            restore that one to undo this operation.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground italic">Loading…</div>
      ) : backups.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">
          No backups yet. Click "Take backup now" to create your first snapshot.
        </div>
      ) : (
        <div className="border border-border" style={{ borderRadius: 2 }}>
          <table className="w-full text-sm" data-testid="settings-backup-list">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-3 font-medium overline">File</th>
                <th className="text-left p-3 font-medium overline">Created (Sydney)</th>
                <th className="text-left p-3 font-medium overline">Size</th>
                <th className="text-right p-3 font-medium overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.filename} className="border-b border-border last:border-0">
                  <td className="p-3 font-mono text-xs">{b.filename}</td>
                  <td className="p-3 text-muted-foreground">{fmtDate(b.created_at)}</td>
                  <td className="p-3 text-muted-foreground">{fmtSize(b.size_bytes)}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadBackup(b.filename)}
                      disabled={downloading === b.filename}
                      className="mr-2"
                      data-testid={`settings-backup-download-${b.filename}`}
                      style={{ borderRadius: 2 }}
                    >
                      {downloading === b.filename ? "Downloading…" : "Download"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteBackup(b.filename)}
                      disabled={deleting === b.filename}
                      data-testid={`settings-backup-delete-${b.filename}`}
                      style={{ borderRadius: 2 }}
                    >
                      {deleting === b.filename ? "Deleting…" : "Delete"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {guideOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setGuideOpen(false)}
          data-testid="settings-backup-guide-modal"
        >
          <div
            className="bg-card border border-border max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            style={{ borderRadius: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <div className="overline">Restore playbook</div>
                <div className="font-serif text-lg tracking-tight" style={{ fontWeight: 450 }}>
                  Deploy-day safety guide
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={emailGuide}
                  disabled={emailing}
                  style={{ borderRadius: 2 }}
                  data-testid="settings-backup-guide-email"
                >
                  {emailing ? "Sending…" : "Email to me"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(guideMd);
                    toast.success("Copied to clipboard");
                  }}
                  disabled={!guideMd}
                  style={{ borderRadius: 2 }}
                  data-testid="settings-backup-guide-copy"
                >
                  Copy markdown
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGuideOpen(false)}
                  style={{ borderRadius: 2 }}
                  data-testid="settings-backup-guide-close"
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              {guideLoading ? (
                <div className="text-sm text-muted-foreground italic">Loading…</div>
              ) : (
                <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono">
                  {guideMd}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function SetPasswordCard() {
  const [pw, setPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (!pw || pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/set-password", { password: pw });
      toast.success(data.message || "Password set successfully");
      setPw(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set password");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 max-w-md">
      <div>
        <Label className="overline">New password</Label>
        <Input
          type="password" value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Min 8 characters"
          className="mt-1 input-crisp font-mono"
          data-testid="settings-new-password"
        />
      </div>
      <div>
        <Label className="overline">Confirm password</Label>
        <Input
          type="password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mt-1 input-crisp font-mono"
          data-testid="settings-confirm-password"
        />
      </div>
      <Button
        onClick={submit}
        disabled={saving || !pw || pw !== confirm}
        data-testid="settings-set-password-btn"
      >
        {saving ? "Saving…" : "Set password"}
      </Button>
    </div>
  );
}
